import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useMemo } from "react";
import SalonService from "../../services/SalonService";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./CalendarStyles.css";
import moment from "moment";
import "./AppointmentBooking.css";
import "../../style.css";
import LoadingIndicator from '../loadingIndicator/LoadingIndicator';
import ShowSalonImages from "../salonImages/ShowSalonImages";
import { useSalon } from "../../context/SalonContext";
import { toast } from "react-toastify";
import categoryIcons from "./ServiceCategoryIcons";
import ReviewsCard from "../reviews/ReviewsCard";
import AllReviewsModal from "../reviews/AllReviewsModal";
import {
    FaMapMarkerAlt, FaPhoneAlt, FaClock, FaCheckCircle,
    FaStar, FaChevronRight, FaTimes, FaLayerGroup, FaCut,
    FaChevronDown, FaChevronUp
} from "react-icons/fa";

const AppointmentBooking = () => {
    const { salonUuid, customerId, salonName } = useSalon();
    const navigate = useNavigate();
    const currentDate = new Date();
    const [data, setData] = useState(null);
    const [salonImages, setSalonImages] = useState(null);
    const [reviewSummary, setReviewSummary] = useState(null);
    const [showReviewsModal, setShowReviewsModal] = useState(false);

    const today = moment().format('YYYY-MM-DD');
    const oneMonthLater = moment().add(1, "months").format("YYYY-MM-DD");
    const [appointmentDate, setAppointmentDate] = useState(today);
    const [loading, setLoading] = useState(true);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [categoriesData, setCategoriesData] = useState([]);
    const [packagesData, setPackagesData] = useState([]);
    const [activeTab, setActiveTab] = useState("services"); // "services" or "packages"
    const [expandedCategories, setExpandedCategories] = useState({});
    const [isReviewsExpanded, setIsReviewsExpanded] = useState(false);

    const [isTodayHoliday, setIsTodayHoliday] = useState(false);
    const [salonEmps, setSalonEmps] = useState([]);
    const [slots, setSlots] = useState([]);
    const [bkgAvlMsg, setBkgAvlMsg] = useState(null);
    const [bookApntDisable, setBookApntDisable] = useState(false);
    const [disabledDates, setDisabledDates] = useState([]);
    const [customerMobileNumber, setCustomerMobileNumber] = useState("");

    const [empId, setEmpId] = useState(null);
    const [empName, setEmpName] = useState(null);

    const [advanceSummary, setAdvanceSummary] = useState(null);
    const [showSlotwiseEmps, setShowSlotwiseEmps] = useState(false);
    const [shopOpensAt, setShopOpensAt] = useState(null);
    const [shopClosesAt, setShopClosesAt] = useState(null);
    let timeInMinutes = (currentDate.getHours() * 60) + currentDate.getMinutes();
    const [serviceAtHome, setServiceAtHome] = useState(false);
    const pressTimer = useRef(null);
    const [showFullAbout, setShowFullAbout] = useState(false);
    const MAX_ABOUT_LENGTH = 100;

    const timeoutRef = useRef(null);

    const servicesData = useMemo(() =>
        categoriesData.flatMap(cat => cat.services || []),
        [categoriesData]
    );

    const selectedServices = useMemo(() =>
        servicesData.filter(s => s.checked),
        [servicesData]
    );

    const selectedPackages = useMemo(() =>
        packagesData.filter(p => p.checked),
        [packagesData]
    );

    const serviceIds = useMemo(() =>
        selectedServices.map(s => s.id),
        [selectedServices]
    );

    const packageIds = useMemo(() =>
        selectedPackages.map(p => p.id),
        [selectedPackages]
    );

    const serviceTotal = useMemo(() =>
        selectedServices.reduce((sum, s) => sum + (s.servicePrice || 0), 0) +
        selectedPackages.reduce((sum, p) => sum + (p.price || 0), 0),
        [selectedServices, selectedPackages]
    );

    useEffect(() => {
        const isLoggedIn = localStorage.getItem("ACCESS_TOKEN");
        if (!isLoggedIn) {
            navigate(`/login?redirect=/booking`);
            return;
        }

        let mySalonUuid = salonUuid ? salonUuid : localStorage.getItem("SALON_UUID");
        if (!mySalonUuid) {
            navigate("/home");
        } else {
            getSalonDetails(mySalonUuid);
            getSalonImages(mySalonUuid);
            getSalonServices(mySalonUuid);
            getSalonEmps('datewise', today, null, mySalonUuid);
            fetchReviewSummary(mySalonUuid);
            setCustomerMobileNumber(localStorage.getItem("MOBILE_NUMBER"));
        }

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [navigate, today, salonUuid]);

    useEffect(() => {
        if (!salonUuid || !appointmentDate || !selectedSlot || serviceTotal <= 0) {
            setAdvanceSummary(null);
            return;
        }
        getAdvanceSummary(salonUuid, serviceTotal);
    }, [salonUuid, appointmentDate, selectedSlot, serviceTotal]);

    async function getSalonDetails(id) {
        setLoading(true);
        try {
            const response = await SalonService.getSalonDataById(id, appointmentDate);
            const salonData = response?.data;
            if (salonData) {
                setData(salonData);
                setSlots(salonData.slots || []);
                setShopOpensAt(salonData.shopOpensAt);
                setShopClosesAt(salonData.shopClosesAt);
                setDisabledDates(salonData.shopClosingDays || []);
                setIsTodayHoliday(salonData.shopClosingDays.includes(moment(appointmentDate).format('YYYY-MM-DD')));
            }
        } catch (err) {
            toast.error("Failed to load salon details");
        } finally {
            setLoading(false);
        }
    }

    async function getSalonImages(id) {
        try {
            const response = await SalonService.getSalonImages(id);
            setSalonImages(response.data);
        } catch (err) { }
    }

    async function getSalonServices(id) {
        try {
            const response = await SalonService.getSalonServices(id);
            const categories = response.data?.categories || [];

            setCategoriesData(categories.map(cat => ({
                ...cat,
                services: (cat.services || []).map(s => ({ ...s, checked: false }))
            })));

            setPackagesData(response.data?.packages?.map(p => ({ ...p, checked: false })) || []);

            if (categories.length > 0) {
                setExpandedCategories({ [categories[0].categoryId]: true });
            }
        } catch (err) { }
    }

    async function fetchReviewSummary(id) {
        try {
            const res = await SalonService.getInAppReviewsSummary(id);
            setReviewSummary(res.data);
        } catch (err) { }
    }

    async function getSalonEmps(type, date, slot, id) {
        const selectedDate = slot?.apntDate || date || appointmentDate;
        const slotTime = slot?.apntStart || null;
        try {
            const response = await SalonService.fetchSalonEmps(id, selectedDate, slotTime);
            setSalonEmps(response?.data || []);
        } catch (err) { }
    }

    async function getTimeSlots(date) {
        setBkgAvlMsg(null);
        setSelectedSlot(null);
        const formattedDate = moment(date).format('YYYY-MM-DD');
        setAppointmentDate(formattedDate);
        setLoading(true);
        try {
            const response = await SalonService.getSalonDataById(salonUuid || localStorage.getItem('SALON_UUID'), formattedDate);
            const slotsData = response?.data?.slots || [];
            setSlots(slotsData);
            setIsTodayHoliday(response?.data?.shopClosingDays.includes(formattedDate));
        } catch (err) {
            toast.error('Unable to load time slots');
        } finally {
            setLoading(false);
        }
    }

    async function getAdvanceSummary(id, total) {
        try {
            const response = await SalonService.getAdvanceSummary({ salonUuid: id, serviceTotal: total });
            setAdvanceSummary(response?.data);
        } catch (err) { }
    }

    const onToggleService = (serviceId) => {
        setCategoriesData(prev => prev.map(cat => ({
            ...cat,
            services: (cat.services || []).map(s => {
                if (s.id === serviceId) {
                    const nextChecked = !s.checked;
                    if (s.name === 'HOME_SERVICE') setServiceAtHome(nextChecked);
                    return { ...s, checked: nextChecked };
                }
                return s;
            })
        })));
    };

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const preparePackages = (index, row) => {
        const newList = [...packagesData];
        newList[index].checked = !newList[index].checked;
        setPackagesData(newList);
    };

    const selectSlot = (slot) => {
        setSelectedSlot(slot);
        setBkgAvlMsg(slot.empCount > slot.bookedCount ? `${slot.empCount - slot.bookedCount} bookings available` : "Fully booked");
    };

    const handlePressStart = (slot) => {
        pressTimer.current = setTimeout(() => {
            setShowSlotwiseEmps(true);
            getSalonEmps('slotwise', null, slot, salonUuid);
        }, 500);
    };

    const handlePressEnd = () => clearTimeout(pressTimer.current);

    const bookAppointment = async () => {
        if (!selectedSlot) return toast.error('Please select a time slot');
        if (serviceIds.length === 0 && packageIds.length === 0) return toast.error('Please select at least one service or package');

        setLoading(true);
        const payload = {
            salonUuid: salonUuid || localStorage.getItem('SALON_UUID'),
            bookingDate: appointmentDate,
            apntStart: selectedSlot.apntStart,
            apntEnd: selectedSlot.apntEnd,
            serviceIds,
            packageIds,
            mobileNumber: customerMobileNumber,
            slot: selectedSlot.slotTime,
            empId,
            empName,
            advanceRequired: advanceSummary?.advanceRequired || false,
            advanceAmount: advanceSummary?.finalPayable || 0,
            paymentStatus: advanceSummary?.advanceRequired ? "PENDING" : "NOT_REQUIRED",
            totalAmount: serviceTotal,
            timeZone: 'Asia/Kolkata',
            customerId: customerId
        };

        try {
            const res = await SalonService.bookAppointment(payload);
            if (res.data?.bookingRef) {
                if (advanceSummary?.advanceRequired) {
                    navigate(`/pay/${res.data.bookingRef}`, { state: { bookingRef: res.data.bookingRef, amount: advanceSummary.finalPayable } });
                } else {
                    toast.success('Appointment booked successfully!');
                    navigate('/home', { replace: true });
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Booking failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="booking-page-container">
            {loading && <LoadingIndicator />}

            {/* Hero Header with Rating */}
            <header className="salon-header-hero">
                <div className="content-wrapper">
                    <h1 className="header-title">{data?.salonName || "Salon Booking"}</h1>

                    {reviewSummary && reviewSummary.reviewCount > 0 && (
                        <div className="hero-rating-box" onClick={() => setShowReviewsModal(true)}>
                            <FaStar className="me-1 text-warning" />
                            <span className="fw-bold">{Number(reviewSummary.avgRating).toFixed(1)}</span>
                            <span className="ms-2 opacity-75">({reviewSummary.reviewCount} Reviews)</span>
                            <FaChevronRight className="ms-2 small opacity-50" />
                        </div>
                    )}

                    <div className="mobile-accent-diamond">♦</div>
                </div>
            </header>

            {/* Gallery Section */}
            {salonImages?.length > 0 && (
                <div className="gallery-wrapper" style={{ position: 'relative', zIndex: 10 }}>
                    <ShowSalonImages salonImages={salonImages} />
                </div>
            )}

            {/* Step 1: About & Info */}
            <section className="booking-section mt-4">
                <div className="section-title-premium">
                    <span className="step-num">i</span> Salon Details
                </div>
                <div className="salon-details-premium">
                    {data?.address && (
                        <div className="detail-item">
                            <FaMapMarkerAlt className="detail-icon" />
                            <div className="detail-content">
                                <h4>Location</h4>
                                <p>{data.address}</p>
                            </div>
                        </div>
                    )}
                    {data?.comments && (
                        <div className="detail-item">
                            <FaStar className="detail-icon" />
                            <div className="detail-content">
                                <h4>About</h4>
                                <p>
                                    {showFullAbout ? data.comments : `${data.comments.slice(0, MAX_ABOUT_LENGTH)}...`}
                                    <span className="read-more-link" onClick={() => setShowFullAbout(!showFullAbout)}>
                                        {showFullAbout ? " Show less" : " Read more"}
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="row g-3">
                        {data?.mobileNumber && (
                            <div className="col-6">
                                <a href={`tel:${data.mobileNumber}`} className="detail-item text-decoration-none">
                                    <FaPhoneAlt className="detail-icon" />
                                    <div className="detail-content">
                                        <h4>Call</h4>
                                        <p>{data.mobileNumber}</p>
                                    </div>
                                </a>
                            </div>
                        )}
                        <div className="col-6">
                            <div className="detail-item">
                                <FaClock className="detail-icon" />
                                <div className="detail-content">
                                    <h4>Hours</h4>
                                    <p>{isTodayHoliday ? "Holiday" : `${shopOpensAt} - ${shopClosesAt}`}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reviews Summary Section */}
            <section className="booking-section">
                <div
                    className={`section-title-premium clickable-header ${isReviewsExpanded ? 'active' : ''}`}
                    onClick={() => setIsReviewsExpanded(!isReviewsExpanded)}
                >
                    <div className="d-flex align-items-center">
                        <FaStar className="me-2 text-warning" /> Customer Reviews
                    </div>
                    {isReviewsExpanded ? <FaChevronUp className="ms-auto" /> : <FaChevronDown className="ms-auto" />}
                </div>

                {isReviewsExpanded && (
                    <div className="mt-3">
                        <ReviewsCard
                            salonUuid={salonUuid || localStorage.getItem('SALON_UUID')}
                            onOpenAll={() => setShowReviewsModal(true)}
                        />
                    </div>
                )}
            </section>

            {/* Step 2: Services & Packages */}
            <section className="booking-section">
                <div className="section-title-premium">
                    <span className="step-num">1</span> Choose Style
                </div>

                <div className="premium-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
                        onClick={() => setActiveTab('services')}
                    >
                        <FaCut className="me-2" /> Services
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'packages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('packages')}
                    >
                        <FaLayerGroup className="me-2" /> Packages
                    </button>
                </div>

                <div className="services-list-wrapper">
                    {activeTab === 'services' ? (
                        <div className="categories-list">
                            {categoriesData.map((category) => (
                                <div key={category.categoryId} className="category-group-premium">
                                    <div
                                        className={`category-header-premium ${expandedCategories[category.categoryId] ? 'active' : ''}`}
                                        onClick={() => toggleCategory(category.categoryId)}
                                    >
                                        <div className="category-header-left">
                                            <div className="category-icon-wrapper">
                                                <img
                                                    src={categoryIcons[category.categoryId]}
                                                    alt={category.categoryName}
                                                    className="category-icon-img"
                                                    onError={(e) => { e.target.src = categoryIcons['1'] }} // Fallback
                                                />
                                            </div>
                                            <span className="category-name-text">{category.categoryName}</span>
                                            {category.services.filter(s => s.checked).length > 0 && (
                                                <span className="category-selection-badge">
                                                    {category.services.filter(s => s.checked).length}
                                                </span>
                                            )}
                                        </div>
                                        {expandedCategories[category.categoryId] ? <FaChevronUp /> : <FaChevronDown />}
                                    </div>

                                    {expandedCategories[category.categoryId] && (
                                        <div className="category-services-content">
                                            <table className="service-selection-table">
                                                <tbody>
                                                    {category.services.map((service) => (
                                                        <tr key={service.id} className={`service-row-premium ${service.checked ? 'selected' : ''}`} onClick={() => onToggleService(service.id)}>
                                                            <td width="40">
                                                                <input type="checkbox" checked={service.checked} readOnly className="service-checkbox" />
                                                            </td>
                                                            <td>
                                                                <div className="service-info-name">{service.description}</div>
                                                                <div className="service-info-meta">{service.serviceTime} mins</div>
                                                            </td>
                                                            <td className="text-end">
                                                                <div className="service-price-tag">₹{service.servicePrice}</div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <table className="service-selection-table">
                            <tbody>
                                {packagesData.length === 0 ? (
                                    <tr><td colSpan="3" className="text-center py-4 text-muted">No packages available</td></tr>
                                ) : (
                                    packagesData.map((pkg, index) => (
                                        <tr key={pkg.id} className={`service-row-premium ${pkg.checked ? 'selected' : ''}`} onClick={() => preparePackages(index, pkg)}>
                                            <td width="40">
                                                <input type="checkbox" checked={pkg.checked} readOnly className="service-checkbox" />
                                            </td>
                                            <td>
                                                <div className="service-info-name">{pkg.name}</div>
                                                <div className="service-info-meta">{pkg.duration} mins • {pkg.description || "Combo deal"}</div>
                                                {pkg.services && pkg.services.length > 0 && (
                                                    <div className="package-services-list mt-1">
                                                        <span className="small text-muted fw-bold">Includes: </span>
                                                        <span className="small text-muted opacity-75">
                                                            {pkg.services.map(s => s.serviceDescription).join(", ")}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-end">
                                                <div className="service-price-tag">₹{pkg.price}</div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            {/* Step 3: Date */}
            <section className="booking-section">
                <div className="section-title-premium">
                    <span className="step-num">2</span> Select Date
                </div>
                <div className="calendar-wrapper-premium">
                    <Calendar
                        onChange={getTimeSlots}
                        value={new Date(appointmentDate)}
                        tileDisabled={({ date }) => {
                            const d = moment(date).format("YYYY-MM-DD");
                            return d < today || d > oneMonthLater || disabledDates.includes(d);
                        }}
                    />
                </div>
            </section>

            {/* Step 4: Time Slot */}
            {!isTodayHoliday && (
                <section className="booking-section">
                    <div className="section-title-premium">
                        <span className="step-num">3</span> Select Time
                    </div>
                    <div className="slot-grid-premium">
                        {slots.map((slot, index) => (
                            <button
                                key={index}
                                className={`slot-btn-premium ${selectedSlot?.apntStart === slot.apntStart ? "selected" : ""}`}
                                onClick={() => selectSlot(slot)}
                                onMouseDown={() => handlePressStart(slot)}
                                onMouseUp={handlePressEnd}
                                onTouchStart={() => handlePressStart(slot)}
                                onTouchEnd={handlePressEnd}
                                disabled={!slot.slotAvailable}
                            >
                                {slot.slotTime}
                            </button>
                        ))}
                    </div>
                    {bkgAvlMsg && <div className="slot-availability-hint mt-2 text-center small text-primary fw-bold">
                        <FaClock className="me-1" /> {bkgAvlMsg}
                    </div>}

                    <div className="mt-3 text-center">
                        <span className="text-muted small">Long press a slot to see available staff</span>
                    </div>
                </section>
            )}

            {/* Summary & Payment */}
            {(selectedServices.length > 0 || selectedPackages.length > 0) && (
                <div className="summary-card-premium">
                    <h4 className="fw-bold mb-3"><FaCheckCircle className="text-success me-2" /> Booking Summary</h4>
                    <div className="summary-item-row">
                        <span className="label">Date</span>
                        <span className="value">{moment(appointmentDate).format("DD MMM, YYYY")}</span>
                    </div>
                    {selectedSlot && (
                        <div className="summary-item-row">
                            <span className="label">Time Slot</span>
                            <span className="value">{selectedSlot.slotTime}</span>
                        </div>
                    )}
                    {empName && (
                        <div className="summary-item-row">
                            <span className="label">Preferred Staff</span>
                            <span className="value">{empName}</span>
                        </div>
                    )}
                    <div className="summary-divider"></div>
                    {selectedServices.map(s => (
                        <div key={s.id} className="summary-item-row small">
                            <span className="label">{s.description}</span>
                            <span className="value">₹{s.servicePrice}</span>
                        </div>
                    ))}
                    {selectedPackages.map(p => (
                        <div key={p.id} className="summary-item-row small">
                            <span className="label">{p.name} (Pkg)</span>
                            <span className="value">₹{p.price}</span>
                        </div>
                    ))}
                    <div className="summary-divider"></div>
                    <div className="summary-total-row">
                        <span>Total Payable</span>
                        <span>₹{serviceTotal}</span>
                    </div>

                    {advanceSummary?.advanceRequired && (
                        <div className="advance-alert mt-3 p-2 rounded bg-white text-success border border-success small text-center">
                            Advance payment of <strong>₹{advanceSummary.finalPayable}</strong> required to confirm.
                        </div>
                    )}
                </div>
            )}

            {/* Staff Selection Modal */}
            {showSlotwiseEmps && (
                <div className="modal-overlay premium-modal-overlay">
                    <div className="premium-modal-content">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold m-0">Available Staff</h4>
                            <button className="btn p-0" onClick={() => setShowSlotwiseEmps(false)}><FaTimes /></button>
                        </div>
                        <div className="row g-3">
                            {salonEmps.map(emp => (
                                <div key={emp.empId} className="col-4">
                                    <div className={`emp-card-mini ${empId === emp.empId ? 'active' : ''}`} onClick={() => { setEmpId(emp.empId); setEmpName(emp.name); }}>
                                        <div className="emp-img-wrapper" style={{ borderColor: empId === emp.empId ? '#ff4081' : '#eee' }}>
                                            <img src={`data:image/jpeg;base64,${emp.image}`} alt={emp.name} />
                                        </div>
                                        <div className="emp-name-mini">{emp.name}</div>
                                        {empId === emp.empId && <FaCheckCircle className="text-primary mt-1" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="btn btn-primary w-100 mt-4 rounded-pill py-2" onClick={() => setShowSlotwiseEmps(false)}>
                            Confirm Selection
                        </button>
                    </div>
                </div>
            )}

            {/* Sticky Action Button */}
            <div className="booking-footer-sticky">
                <button
                    className="book-now-btn-premium"
                    disabled={bookApntDisable || !selectedSlot || (selectedServices.length === 0 && selectedPackages.length === 0)}
                    onClick={bookAppointment}
                >
                    {advanceSummary?.advanceRequired ? "Pay & Confirm Booking" : "Confirm Booking"}
                    <FaChevronRight className="ms-2" />
                </button>
            </div>

            {/* All Reviews Modal */}
            <AllReviewsModal
                visible={showReviewsModal}
                onClose={() => setShowReviewsModal(false)}
                salonUuid={salonUuid || localStorage.getItem('SALON_UUID')}
            />
        </div>
    );
};

export default AppointmentBooking;
